export const cacheConstant = {
	userData: "USER_DATA",
	configurationSettings: "CONFIGURATION_SETTINGS",
	companyUserList: "COMPANY_USER_LIST",
	userCompany: "USER_COMPANY"
};

type cacheKeys = keyof typeof cacheConstant;

export const TOKEN_BRIDGE_KEY = "__zomentumGetCachedToken__";

export class AppCache {
	constructor() {
		this.updateCacheFromLocalStorage();
		localStorage.removeItem("token");
		this.setupLogoutSync();
		this.bootstrapTokenFromOpener();
		this.exposeTokenGetterForNewTabs();
	}
	data: Partial<Record<cacheKeys, any>> = {};
	private memoryStore: Map<string, any> = new Map();
	private sensitiveKeys = new Set(["token"]);
	private authChannel: BroadcastChannel | null = null;

	private bootstrapTokenFromOpener = () => {
		try {
			if (typeof window === "undefined") {
				return;
			}
			const opener = window.opener as Window | null;
			if (!opener) {
				return;
			}
			const getter = (
				opener as unknown as { [TOKEN_BRIDGE_KEY]?: () => unknown }
			)[TOKEN_BRIDGE_KEY];
			if (typeof getter !== "function") {
				return;
			}
			const token = getter();
			if (token) {
				this.memoryStore.set(
					"token",
					JSON.parse(JSON.stringify(token))
				);
			}
		} catch {
			return;
		}
	};

	private exposeTokenGetterForNewTabs = () => {
		try {
			if (typeof window === "undefined") {
				return;
			}
			(
				window as unknown as { [TOKEN_BRIDGE_KEY]?: () => unknown }
			)[TOKEN_BRIDGE_KEY] = () => {
				const stored = this.memoryStore.get("token");
				return stored ? JSON.parse(JSON.stringify(stored)) : null;
			};
		} catch {
			return;
		}
	};

	private setupLogoutSync = () => {
		try {
			if (typeof BroadcastChannel !== "undefined") {
				this.authChannel = new BroadcastChannel("zomentum-auth");
				this.authChannel.addEventListener("message", event => {
					if (event.data?.type === "LOGOUT") {
						this.handleCrossTabLogout();
					}
				});
			}
		} catch (error) {
			console.warn("BroadcastChannel not supported:", error);
		}
	};

	private handleCrossTabLogout = () => {
		this.memoryStore.clear();
		window.location.href = "/login";
	};

	private broadcastLogout = () => {
		try {
			if (this.authChannel) {
				this.authChannel.postMessage({ type: "LOGOUT" });
			}
		} catch (error) {
			console.warn("Failed to broadcast logout:", error);
		}
	};

	private updateCacheFromLocalStorage = () => {
		this.data = Object.keys(cacheConstant).reduce((total, key) => {
			const currKey = key as cacheKeys;
			const cacheDataString = localStorage.getItem(
				cacheConstant[currKey]
			);
			if (!!!cacheDataString) {
				return total;
			}
			let cacheData = null;
			try {
				cacheData =
					(!!cacheDataString && JSON.parse(cacheDataString)) || null;
			} catch (error) {
				console.error(error);
			}
			total[currKey] = cacheData;
			return total;
		}, {} as any);
	};

	updateReduxKey = (key: cacheKeys, data: any | null) => {
		if (!data) {
			localStorage.removeItem(cacheConstant[key]);
		}
		const stringifiedData = JSON.stringify(data);

		if (!stringifiedData?.length) {
			localStorage.removeItem(cacheConstant[key]);
		}

		localStorage.setItem(cacheConstant[key], stringifiedData);
		this.updateCacheFromLocalStorage();
	};

	get = (key: string, parseJson = false): any => {
		if (this.sensitiveKeys.has(key)) {
			return this.memoryStore.get(key) ?? null;
		}
		let data: any = localStorage.getItem(key) || null;
		if (data && parseJson) {
			try {
				data = JSON.parse(data);
			} catch (error) {
				console.error(
					`Error: ${error} :: key: ${key} :: data: ${data}`
				);
			}
		}
		return data;
	};
	save = (key: string, data: any) => {
		if (this.sensitiveKeys.has(key)) {
			this.memoryStore.set(key, JSON.parse(JSON.stringify(data)));
			return;
		}
		localStorage.setItem(key, JSON.stringify(data));
	};
	deleteItem = (key: string) => {
		this.memoryStore.delete(key);
		localStorage.removeItem(key);
	};
	deleteAll = () => {
		this.memoryStore.clear();
		localStorage.clear();
		this.broadcastLogout();
	};
}

export default new AppCache();
